import { BitmapText, Container, Sprite, Text, type Texture } from "pixi.js";
import { BITMAP_FONTS, FONT } from "../views/textStyles";
import { LINES_LAYOUT } from "./layout";

/** An arrow the player cannot use now (the bet locked: a round plays, autoplay runs, a replay). */
const ARROW_OFF_ALPHA = 0.35;

/**
 * The LINES selector under the grid in the base game (decision 56), after 3 Coin Volcanoes': its name on a tab, the
 * count in a red window, ‹ and › on either side. It draws what its presenter tells it and answers which arrow a point
 * lies on; the taps are the shell's surface over the scene (`LineSelectorPresenter`).
 */
export class LineSelectorView extends Container {
  private readonly title: Text;
  private readonly count: BitmapText;
  private readonly left: Sprite;
  private readonly right: Sprite;

  public constructor(input: { panel: Texture | null; arrow: Texture | null; title: string }) {
    super({ label: "LineSelector" });
    const { panel: size, name, count, arrow } = LINES_LAYOUT;
    if (input.panel) {
      const panel = new Sprite({ texture: input.panel, anchor: 0.5, label: "Panel" });
      panel.setSize(size.width, size.height);
      this.addChild(panel);
    }
    this.title = new Text({ text: input.title, style: { fontFamily: FONT, fontWeight: "900", fontSize: name.fontSize, fill: 0x5a2600 }, label: "LinesName" });
    this.title.anchor.set(0.5);
    this.title.position.set(0, name.y);
    // The player's language may be longer than LINES: it shrinks into the tab, never grows.
    this.title.scale.set(Math.min(1, name.width / Math.max(1, this.title.width), name.height / Math.max(1, this.title.height)));
    this.count = new BitmapText({ text: "", style: { fontFamily: BITMAP_FONTS.badge, fontSize: count.fontSize }, label: "LinesCount" });
    this.count.anchor.set(0.5);
    this.count.position.set(0, count.y);
    const arrowSprite = (side: -1 | 1, label: string): Sprite => {
      const sprite = new Sprite({ texture: input.arrow ?? undefined, anchor: 0.5, label });
      sprite.setSize(arrow.size, arrow.size);
      // The art points right: the left one is it mirrored.
      sprite.scale.x *= side;
      sprite.position.set(side * arrow.x, arrow.y);
      return sprite;
    };
    this.left = arrowSprite(-1, "LinesLess");
    this.right = arrowSprite(1, "LinesMore");
    this.addChild(this.title, this.left, this.right, this.count);
  }

  /** The selector hangs from the frame's bottom edge. */
  public hangFrom(frameBottom: number): void {
    this.position.set(0, frameBottom + LINES_LAYOUT.belowFrame);
  }

  /** The count, and which arrows the player can use. */
  public setState(state: { lines: number; canDecrease: boolean; canIncrease: boolean }): void {
    this.count.text = String(state.lines);
    this.left.alpha = state.canDecrease ? 1 : ARROW_OFF_ALPHA;
    this.right.alpha = state.canIncrease ? 1 : ARROW_OFF_ALPHA;
  }

  /** The arrow a point in the canvas's global space lies on: -1 for ‹, +1 for ›, null elsewhere. */
  public arrowAt(point: { x: number; y: number }): -1 | 1 | null {
    if (!this.visible) return null;
    if (this.left.getBounds().containsPoint(point.x, point.y)) return -1;
    if (this.right.getBounds().containsPoint(point.x, point.y)) return 1;
    return null;
  }

  /** The count shown (the dev hook's read). */
  public get shownLines(): number {
    return Number(this.count.text);
  }

  /** Whether each arrow is lit (the dev hook's read). */
  public get arrows(): { less: boolean; more: boolean } {
    return { less: this.left.alpha === 1, more: this.right.alpha === 1 };
  }
}
